import { useEffect, useMemo, type FC, type ReactNode } from "react";
import { ScopedCssBaseline } from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import { useCurrentPlatformQuery } from "@/partner/entities/platforms";
import {
  createPlatformTheme,
  DEFAULT_FONTS,
  buildGoogleFontsUrl,
  parseFontFamily,
} from "@/partner/shared/platformTheme";

/**
 * Тема платформы внутри контейнера предпросмотра. Снаружи остаётся тёмная
 * тема кабинета: ScopedCssBaseline применяет сброс и фон только к своему
 * поддереву, в отличие от глобального CssBaseline витрины.
 *
 * Шрифты: в кабинете нет next/font, поэтому дефолтные Archivo и Manrope
 * подключаются той же ссылкой на Google Fonts, что и кастомные шрифты
 * платформы. Ссылка вставляется в <head> один раз на документ, по href.
 */
export const PlatformThemeProvider: FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { data } = useCurrentPlatformQuery();
  const platformTheme = data?.currentPlatform?.theme;

  const theme = useMemo(
    () => createPlatformTheme(platformTheme, DEFAULT_FONTS),
    [platformTheme]
  );

  const googleFontsUrl = useMemo(() => {
    const families = [
      parseFontFamily(platformTheme?.typography?.headingFontFamily)?.family,
      parseFontFamily(platformTheme?.typography?.fontFamily)?.family,
      DEFAULT_FONTS.heading,
      DEFAULT_FONTS.base,
    ].filter((family): family is string => !!family);
    return buildGoogleFontsUrl(Array.from(new Set(families)));
  }, [platformTheme]);

  useEffect(() => {
    if (!googleFontsUrl) {
      return;
    }

    if (
      document.head.querySelector(
        `link[data-platform-fonts][href="${googleFontsUrl}"]`
      )
    ) {
      return;
    }

    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = googleFontsUrl;
    link.dataset.platformFonts = "true";
    document.head.appendChild(link);

    // Убираем только ту ссылку, которую создали сами: если ссылка уже была
    // в <head> (ветка выше), это не наш элемент, и трогать его нельзя.
    return () => {
      link.remove();
    };
  }, [googleFontsUrl]);

  return (
    <ThemeProvider theme={theme}>
      <ScopedCssBaseline sx={{ height: "100%" }}>{children}</ScopedCssBaseline>
    </ThemeProvider>
  );
};
